/**
 * Champion Application Service
 * Handles champion application submissions to unified multi-city API
 */

// @ts-ignore - Vite env vars
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || 'https://api.toolboxlogic.com';

export interface ChampionApplicationData {
  applicantName: string;
  email: string;
  phone?: string;
  organizations?: string[];
  motivation: string;
  links?: {
    facebook?: string;
    linkedin?: string;
    twitter?: string;
    website?: string;
    other?: string;
  };
  communityInfluence?: string;
  availability?: string;
}

export interface ChampionApplicationResponse {
  success: boolean;
  error?: string;
  data?: {
    id: string;
    market_key: string;
    created_at: string;
  };
}

export async function submitChampionApplication(
  formData: any,
): Promise<ChampionApplicationResponse> {
  try {
    // Transform form data to match backend API expectations
    // Normalize organizations into an array
    const organizationsArray = Array.isArray((formData as any).organizations)
      ? ((formData as any).organizations as string[])
      : ((formData.organizations || '') as string)
          .split(/[\n,]+/)
          .map((s: string) => s.trim())
          .filter(Boolean);

    const transformedData = {
      applicantName: formData.name,
      email: formData.email,
      phone: formData.phone || null,
      organizations: organizationsArray,
      motivation: formData.platformDescription,
      links: {
        facebook: null,
        linkedin: null,
        twitter: null,
        website: null,
        other: formData.socialMediaFollowing,
      },
      communityInfluence: formData.communityConnections,
      availability: formData.availability,
      // Note: reference fields (reference1Name, reference1Contact, etc.) are commented out
      // and not sent to the backend as they are not supported by the current API
    };

    const response = await fetch(
      `${API_BASE_URL}/api/v1/louisiana/lake-charles/champion-application`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transformedData),
      },
    );

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.message || `Server error: ${response.status}`,
      };
    }

    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    console.error('Champion application submission error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error occurred',
    };
  }
}
